// Principles page translations
const principlesTranslations = {
  en: {
    hero: {
      pill: "principles / thewhizkind",
      title: "a few things we believe in",
      subtitle: [
        "We don't have a 97-page corporate handbook.",
        "What we do have are a few ideas about people, creativity, technology and how we'd like to exist in the world. They shape what we make, who we work with and the decisions we make along the way."
      ]
    },
    principles: [
      {
        title: "Humanity",
        body: [
          "We live on a small rock travelling through space, orbiting a giant ball of plasma, in a universe we barely understand.",
          "And, as far as we know, this is the only place where consciousness has looked around and wondered what any of it means.",
          "That's kind of absurd.",
          "And kind of incredible.",
          "So we value people. Their stories, cultures, differences, ideas, weirdness, time, and the tiny fraction of cosmic history we happen to share.",
          "Work matters. Design matters. Technology matters.",
          "But none of it matters very much without people.",
          { strong: "People first. Always." }
        ]
      },
      {
        title: "Different is good",
        body: [
          "The world would be painfully boring if everyone thought, looked, spoke and created in the same way.",
          "We value different cultures, backgrounds, identities, perspectives and ways of thinking. Not because diversity looks good on a company page, but because we genuinely believe different people make ideas better.",
          "You don't have to fit a particular mould to belong here.",
          "There isn't one."
        ]
      },
      {
        title: "Kindness isn't unprofessional",
        body: [
          "We take our work seriously.",
          "We don't think that means we have to take ourselves too seriously.",
          "Behind every project, deadline, email, meeting and invoice is a human being with a life outside work.",
          "People have good days. Bad days. Families. Birthdays. Holidays. Problems. 3 a.m. ideas. And occasionally emails they completely forget to reply to.",
          "Professionalism doesn't have to mean being cold.",
          "We'd rather work with patience, honesty, empathy and a little humour."
        ]
      },
      {
        title: "Make it accessible",
        body: [
          "Good design shouldn't create unnecessary barriers.",
          "We try to make the websites, tools and experiences we create understandable and usable by as many people as reasonably possible.",
          "We won't always get everything right.",
          "When we don't, we'd rather learn and improve than pretend we did."
        ]
      },
      {
        title: "Stay curious",
        body: [
          "Some things are worth exploring simply because someone asked:",
          { strong: "'What if we tried this?'" },
          "We like experiments, strange ideas, new technologies and projects whose usefulness isn't immediately obvious.",
          "Not every experiment needs a business model.",
          "Not every idea needs a five-year roadmap.",
          "Sometimes the only good reason to try something is: fuck it, let's see what happens."
        ]
      },
      {
        title: "Humans + AI",
        body: [
          "We like AI.",
          "Quite a lot, actually.",
          "But we don't think technology makes people less important.",
          "We use AI to explore, experiment, automate boring things and occasionally make things that would have been ridiculous to attempt just a few years ago.",
          "But judgement, responsibility, empathy, taste and creativity remain human.",
          "Technology should give people more possibilities, not make people feel disposable."
        ]
      },
      {
        title: "The internet should be fun",
        body: [
          "Somewhere along the way, a lot of the internet started looking the same.",
          [
            "Same layouts. Same gradients. Same perfectly optimised buttons asking you to ",
            { strong: "get started" },
            "."
          ],
          "We think there should still be room for experiments, personality, weird little tools, unnecessary animations and things that exist simply because someone thought they'd be fun to make.",
          "Not everything needs to convert. Sometimes you can make something just because it's cool.",
          "Some things can just make the internet a slightly more interesting place."
        ]
      },
      {
        title: "No unnecessary bullshit",
        body: [
          "We like simple language, honest conversations and knowing what we're actually trying to accomplish.",
          "Complicated words don't automatically make ideas smarter.",
          "More meetings don't automatically make projects better.",
          "And adding 'AI-powered next-generation ecosystem' to something doesn't necessarily make it one.",
          "If something can be simpler, we'd rather make it simpler."
        ]
      },
      {
        title: "Relationships over transactions",
        body: [
          "Of course, we're a business.",
          "There are projects, contracts, invoices and budgets.",
          "But we'd rather not reduce every relationship to a transaction.",
          "We like getting to know the people behind the work. Some collaborations last a few weeks. Some last years. Some turn into friendships. Some are simply a good conversation.",
          "That's okay.",
          "Good work is better when there's trust on both sides."
        ]
      },
      {
        title: "More than money",
        body: [
          "At some point, a lot of what we call success gets reduced to a number sitting in a bank account.",
          "It's a useful number. We'd be lying if we said it didn't matter.",
          "We just don't think it should decide everything.",
          "Some things are worth doing because they mean something. Some because they help someone. Some because they're interesting, strange or challenging. And some simply because we'd like them to exist in the world.",
          "A bigger number is nice.",
          "A better story is nicer."
        ]
      },
      {
        title: "Be good to each other",
        body: [
          "We're here for an absurdly short amount of time.",
          "On one tiny planet.",
          "In an unimaginably large universe.",
          "We might as well make interesting things, help each other when we can, stay curious, disagree without being horrible about it and leave things a little better than we found them.",
          { strong: "Make good things. Work with good people. Be a good human." },
          "That's pretty much it."
        ]
      }
    ]
  },
  de: {
    hero: {
      pill: "prinzipien / thewhizkind",
      title: "einige dinge, an die wir glauben",
      subtitle: [
        "wir haben kein 97-seitiges unternehmenshandbuch.",
        "was wir haben, sind einige ideen über menschen, kreativität, technologien und wie wir in der welt existieren möchten. sie prägen, was wir erschaffen, mit wem wir arbeiten und die entscheidungen, die wir unterwegs treffen."
      ]
    },
    principles: [
      {
        title: "menschlichkeit",
        body: [
          "wir leben auf einem kleinen felsen, der durch den weltraum reist, der eine riesige plasmaskugel umkreist, in einem universum, das wir kaum verstehen.",
          "und soweit wir wissen, ist dies der einzige ort, an dem sich bewusstsein hervorgetan, umgesehen und gefragt hat, was das alles bedeutet.",
          "das klingt irgendwie absurd.",
          "und irgendwie unglaublich.",
          "daher schätzen wir menschen. ihre geschichten, kulturen, unterschiede, ideen, eigenheiten, zeit und den winzigen bruchteil der kosmischen geschichte, den wir zufällig teilen.",
          "arbeit ist wichtig. design ist wichtig. technologie ist wichtig.",
          "aber nichts davon ist ohne menschen besonders relevant.",
          { strong: "menschen zuerst. immer." }
        ]
      },
      {
        title: "verschiedenheit ist gut",
        body: [
          "die welt wäre unerträglich langweilig, wenn alle gleich dächten, aussähen, sprächen und die gleichen dinge kreierten.",
          "wir schätzen verschiedene kulturen, hintergründe, identitäten, perspektiven und denkweisen. nicht, weil vielfalt auf einer unternehmensseite gut aussieht, sondern weil wir wirklich glauben, dass unterschiedliche menschen ideen besser machen.",
          "du musst nicht in ein bestimmtes schema passen, um hierher zu gehören.",
          "es gibt keins."
        ]
      },
      {
        title: "freundlichkeit ist nicht unprofessionell",
        body: [
          "wir nehmen unsere arbeit ernst.",
          "wir denken nicht, dass das bedeutet, dass wir uns selbst zu ernst nehmen sollten.",
          "hinter jedem projekt, jeder deadline, jeder e-mail, jedem meeting und jeder rechnung steht ein mensch mit einem leben abseits der arbeit.",
          "menschen haben gute tage. schlechte tage. familien. geburtstage. feiertage. probleme. ideen um 3 uhr morgens. und gelegentlich e-mails, die sie komplett zu beantworten versäumen.",
          "professionalität bedeutet nicht, kalt zu sein.",
          "wir arbeiten lieber mit geduld, ehrlichkeit, einfühlungsvermögen und einer prise humor."
        ]
      },
      {
        title: "mach es barrierefrei",
        body: [
          "gutes design sollte keine unnötigen barrieren schaffen.",
          "wir versuchen, die websites, werkzeuge und digitalen inhalte, die wir erbauen, in einem vernünftigen rahmen für so viele menschen wie möglich verständlich und nutzbar zu machen.",
          "wir werden nicht immer alles richtig machen.",
          "wenn wir es mal nicht tun, ziehen wir es vor, aus den fehlern zu lernen und uns zu verbessern, statt so zu tun, als hätten wir alles problemlos geschafft."
        ]
      },
      {
        title: "bleib neugierig",
        body: [
          "einige dinge sind es bereits deshalb wert erkundet zu werden, weil jemand gefragt hat:",
          { strong: "'was wäre, wenn wir das versuchen?'" },
          "wir mögen experimente, seltsam anmutende ideen, neue technologien und projekte, deren nützlichkeit nicht sofort offensichtlich ist.",
          "nicht jedes experiment braucht ein geschäftsmodell.",
          "nicht jede idee braucht einen fünf-jahres-plan.",
          "manchmal ist der einzige gute grund, etwas auszuprobieren: scheiß drauf, lass uns sehen, was daraus wird."
        ]
      },
      {
        title: "menschen + ki",
        body: [
          "wir mögen ki.",
          "tatsächlich sehr.",
          "aber wir denken nicht, dass technologie menschen weniger wichtig macht.",
          "wir nutzen ki, um zu erkunden, zu experimentieren, langweilige dinge zu automatisieren und gelegentlich dinge zu erschaffen, deren umsetzung vor einigen jahren noch absurd gewesen wäre.",
          "aber urteilsvermögen, verantwortung, einfühlungsvermögen, geschmack und kreativität bleiben menschlich.",
          "technologie sollte menschen mehr möglichkeiten eröffnen, nicht dazu genutzt werden, dass sich menschen überflüssig fühlen."
        ]
      },
      {
        title: "das internet sollte spaß machen",
        body: [
          "irgendwann begann ein großer teil des internets, gleich auszusehen.",
          [
            "gleiche layouts. gleiche farbverläufe. gleiche perfekt optimierte buttons, die dich auffordern, ",
            { strong: "loszulegen" },
            "."
          ],
          "wir denken, dass es immer noch platz für experimente, persönlichkeit, eigentümliche kleine werkzeuge, unnötige animationen und dinge geben sollte, einfach weil jemand lust darauf hatte sie zu bauen und die dabei lustig zu erstellen wären.",
          "nicht alles muss sich perfekt einfügen. manchmal kann man etwas einfach deshalb schaffen, weil es cool ist.",
          "einige dinge können das internet einfach zu einem etwas interessanteren ort machen."
        ]
      },
      {
        title: "kein unnötiger unsinn",
        body: [
          "wir mögen einfache sprache, ehrliche gespräche und sind auf der suche danach, was wir tatsächlich erreichen wollen.",
          "komplizierte worte machen ideen nicht automatisch klüger.",
          "mehr meetings machen projekte nicht automatisch besser.",
          "und das hinzufügen des labels 'ki-gesteuertes next-generation-ökosystem' macht etwas nicht unbedingt zu einem.",
          "wenn etwas einfacher sein kann, würden wir es lieber einfacher machen."
        ]
      },
      {
        title: "beziehungen vor transaktionen",
        body: [
          "natürlich sind wir ein unternehmen.",
          "es gibt projekte, verträge, rechnungen und budgets.",
          "aber wir möchten nicht jede beziehung auf eine transaktion reduzieren.",
          "wir mögen es, die menschen hinter der arbeit kennenzulernen. einige kooperationen dauern ein paar wochen. einige dauern jahre. einige entwickeln sich zu freundschaften. einige bestehen einfach aus einem guten und erhellenden gespräch.",
          "das ist in ordnung.",
          "gute arbeit ist besser, wenn auf beiden seiten vertrauen herrscht."
        ]
      },
      {
        title: "mehr als geld",
        body: [
          "irgendwann wird ein großer teil dessen, was wir erfolg nennen, auf eine zahl auf einem bankkonto reduziert.",
          "es ist eine nützliche zahl. wir würden lügen, wenn wir das leugnen würden.",
          "wir denken nur nicht, dass sie alles entscheiden sollte.",
          "einige dinge sind es wert, getan zu werden, weil sie etwas bedeuten. einige, weil sie jemandem helfen. einige, weil sie interessant, kurios oder herausfordernd sind. und einige einfach, weil wir möchten, dass sie in die welt hinausgebracht werden.",
          "eine größere zahl ist schön.",
          "eine schöne geschichte ist schöner."
        ]
      },
      {
        title: "seid gut zueinander",
        body: [
          "wir sind für eine absurd kurze zeit.",
          "hier auf einem winzigen planeten.",
          "in einem unvorstellbar großen universum.",
          "wir könnten genauso gut interessante dinge schaffen, einander helfen, wo wir können, neugierig bleiben, anders denken, ohne dabei grausam zueinander zu sein, und die dinge ein bisschen besser hinterlassen, als wir sie vorgefunden haben.",
          { strong: "gute dinge schaffen. mit guten menschen arbeiten. ein guter mensch sein." },
          "das ist im grunde alles."
        ]
      }
    ]
  }
};

export default principlesTranslations;